// The Day Book as a printable A4 page (Reports → Day book → Print). Pure:
// report in, self-contained HTML out (inline styles, no scripts — it is
// printed from a hidden frame by lib/print.ts). Same figures as the screen.
import { escapeHtml as e } from '@/lib/print';
import type { DayBookMoney, DayBookReport } from './types';

const inr = (v: number | string | null | undefined) =>
  `₹${(Number(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const day = (v: string) => new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const MONEY_HEAD = '<th>Bills</th><th>Gross</th><th>Discount</th><th>Net billed</th><th>Collected</th><th>Credited</th><th>Outstanding</th>';
const moneyCells = (r: DayBookMoney) =>
  `<td>${r.bill_count}</td><td>${inr(r.gross)}</td><td>${inr(r.discount)}</td><td>${inr(r.net_billed)}</td>` +
  `<td>${inr(r.collected)}</td><td>${inr(r.credited)}</td><td>${inr(r.outstanding)}</td>`;

export function dayBookPrintHtml(report: DayBookReport, opts: {
  hospitalName: string;
  branchName?: string | null;
  departmentLabel?: string | null;
  printedBy?: string | null;
}): string {
  const b = report.billing;
  const s = b.summary;
  const period = report.from === report.to ? day(report.from) : `${day(report.from)} – ${day(report.to)}`;
  const register = b.register.map((r) =>
    `<tr><td class="l mono">${e(r.bill_number)}</td><td class="l">${e(new Date(r.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }))}</td>` +
    `<td class="l">${e(r.patient_name ?? '')}</td><td class="l">${e(r.setting)}</td><td>${inr(r.gross)}</td><td>${inr(r.discount)}</td>` +
    `<td>${inr(r.net)}</td><td>${inr(r.balance)}</td></tr>`).join('');

  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><title>Day book ${e(period)}</title>
<style>
  @page { size: A4; margin: 12mm; }
  body { margin: 0; font-family: Arial, Helvetica, sans-serif; font-size: 9.5pt; color: #111; }
  h1 { font-size: 15pt; margin: 0; } h2 { font-size: 11pt; margin: 14px 0 4px; border-bottom: 1px solid #999; padding-bottom: 2px; }
  .head { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #111; padding-bottom: 6px; }
  .muted { color: #555; font-size: 8.5pt; }
  .tiles { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; margin-top: 8px; }
  .tile { border: 1px solid #bbb; padding: 5px 7px; } .tile b { display: block; font-size: 12.5pt; }
  table { width: 100%; border-collapse: collapse; margin-top: 4px; }
  th, td { border: 1px solid #ccc; padding: 3px 5px; text-align: right; }
  th { background: #f1f1f1; font-size: 8.5pt; } .l { text-align: left; } .mono { font-family: 'Courier New', monospace; }
  tr.total td { font-weight: bold; background: #fafafa; } tr { break-inside: avoid; }
</style></head><body>
<div class="head">
  <div><h1>${e(opts.hospitalName)} — Day book</h1>
    <div class="muted">${e(opts.branchName ? `Branch: ${opts.branchName} · ` : '')}${e(period)}${opts.departmentLabel ? ` · Register: ${e(opts.departmentLabel)}` : ''}</div></div>
  <div class="muted">Printed ${e(new Date().toLocaleString('en-IN'))}${opts.printedBy ? ` by ${e(opts.printedBy)}` : ''}</div>
</div>

<div class="tiles">
  <div class="tile">Total billed<b>${inr(s.net_billed)}</b><span class="muted">${s.bill_count} bills · gross ${inr(s.gross)}</span></div>
  <div class="tile">Discounts<b>${inr(s.discount)}</b><span class="muted">${s.loyalty > 0 ? `+ ${inr(s.loyalty)} loyalty` : '&nbsp;'}</span></div>
  <div class="tile">Collected<b>${inr(s.collected)}</b><span class="muted">net ${inr(s.net_collected)} after ${inr(s.credited)} credited</span></div>
  <div class="tile">Outstanding dues<b>${inr(s.outstanding)}</b><span class="muted">patients ${inr(s.patient_outstanding)} · insurers ${inr(s.insurer_outstanding)}</span></div>
</div>

<h2>OP / IP billing</h2>
<table><thead><tr><th class="l">Setting</th>${MONEY_HEAD}</tr></thead><tbody>
${b.by_setting.map((r) => `<tr><td class="l">${e(r.label)}</td>${moneyCells(r)}</tr>`).join('')}
<tr class="total"><td class="l">All</td>${moneyCells(s)}</tr></tbody></table>

<h2>By department</h2>
<table><thead><tr><th class="l">Department</th>${MONEY_HEAD}</tr></thead><tbody>
${b.by_department.map((r) => `<tr><td class="l">${e(r.label)}</td>${moneyCells(r)}</tr>`).join('')}
<tr class="total"><td class="l">Total — all departments</td>${moneyCells(s)}</tr></tbody></table>
<div class="muted">A bill with lines in several departments counts once in "Bills" for each; money is split by its lines, so the rupee columns add up exactly.</div>

<h2>Collections</h2>
<table><thead><tr><th class="l">Payment mode</th><th>Payments</th><th>Amount</th></tr></thead><tbody>
${report.by_payment_mode.map((m) => `<tr><td class="l">${e(m.payment_mode)}</td><td>${m.payment_count}</td><td>${inr(m.amount)}</td></tr>`).join('')}
</tbody></table>

<h2>Insurance</h2>
<table><tbody>
<tr><td class="l">Billed to insurers (bills raised in the period)</td><td>${inr(b.insurance.billed_to_insurers)}</td></tr>
<tr><td class="l">Collected from insurers</td><td>${inr(b.insurance.collected_from_insurers)}</td></tr>
<tr><td class="l">TDS withheld · written off</td><td>${inr(b.insurance.tds_withheld)} · ${inr(b.insurance.written_off)}</td></tr>
<tr><td class="l">Owed by insurers at the end of the period</td><td>${inr(b.insurance.insurer_outstanding)}</td></tr>
<tr><td class="l">Claims opened · submitted (amount) · settlements received</td><td>${b.insurance.claims_opened} · ${b.insurance.claims_submitted} (${inr(b.insurance.claims_submitted_amount)}) · ${b.insurance.settlements_received}</td></tr>
</tbody></table>

<h2>Bills raised${opts.departmentLabel ? ` — ${e(opts.departmentLabel)}` : ''}</h2>
${b.register.length ? `<table><thead><tr><th class="l">Bill no.</th><th class="l">Date</th><th class="l">Patient</th><th class="l">OP/IP</th><th>Gross</th><th>Discount</th><th>Net</th><th>Balance</th></tr></thead><tbody>${register}</tbody></table>` : '<div class="muted">No bills in this period.</div>'}
${b.register_truncated ? '<div class="muted">Only the first 1,000 bills are listed — export CSV or narrow the dates for the rest.</div>' : ''}
</body></html>`;
}
